import axios from "@/plugins/axios";
import SAMPLE from "./kemudahanAsasSampleData";
import DAERAH_SAMPLE from "./kemudahanAsasDaerahSampleData";
import KAUM_SAMPLE from "./kemudahanAsasKaumSampleData";

// Kemudahan Asas (Jadual Awalan) — Peratusan isi rumah mengikut kemudahan asas,
// strata dan daerah pentadbiran. One service backs all four amenity pages
// (JMR / Bekalan Air / Bekalan Elektrik / Kutipan Sampah); the amenity's page
// passes its own controller endpoint. Values are percentages (%) of households.
//
// Response shape consumed by the pages:
//   data.years : [Number]
//   data.rows  : [{ ng?, dp?, kategori, group, isTotal?, values:{ <sub>:{ <year>:pct } } }]
//                (pre-ordered; group ∈ "total" | "strata" | "kaum" | "list")
//
// The backend controllers (JadualAwalan/getKemudahanAsasLampiran.js) serve the
// real data: previous years from the precomputed lampiran_negxdaerah(*xstrata /
// *xkaum) tables, the latest year computed live from lampiran_hid_summary_<YEAR>.
// Flip USE_SAMPLE back to true to demo with the bundled xlsx sample data instead.
const USE_SAMPLE = false;

// endpoint → sample bucket key
const ENDPOINT_KEY = {
  getJenisMilikanRumahLampiran: "jmr",
  getBekalanAirLampiran: "air",
  getBekalanElektrikLampiran: "elektrik",
  getKutipanSampahLampiran: "sampah",
};

// Build the ordered `rows` payload for the requested scope from the sample bucket.
// Each row carries a `group` tag so the frontend renders the section headers in the
// same order as Bulanan Purata:
//   Malaysia view : MALAYSIA total → Strata → Kaum → per-negeri list
//   Negeri view   : negeri total   → Strata → Kaum → Daerah Pentadbiran
// Strata + Kaum figures in the sample are per-scope (Malaysia-wide for the Malaysia
// view, per-negeri for a negeri view); the total is the real per-scope figure.
function sampleFor(endpoint, params) {
  const key = ENDPOINT_KEY[endpoint];
  const bucket = SAMPLE[key];
  if (!bucket) return {};
  const years = (bucket.years || []).map(Number);
  const ng = params && params.NG ? String(params.NG) : null;
  const rows = [];

  if (!ng) {
    // Malaysia (all states)
    if (bucket.malaysia) rows.push({ ...bucket.malaysia, group: "total", isTotal: true });
    (bucket.strata || []).forEach(r => rows.push({ ...r, group: "strata" }));
    kaumRows(key, null).forEach(r => rows.push({ ...r, group: "kaum" }));
    (bucket.perNegeri || []).forEach(r => rows.push({ ...r, group: "list" }));
  } else {
    // Single negeri: its total + national strata as a stand-in + its kaum split +
    // its Daerah Pentadbiran breakdown (sample). Optional DP filter narrows the list.
    const neg = (bucket.perNegeri || []).find(r => String(r.ng) === ng);
    if (neg) rows.push({ ...neg, kategori: neg.kategori, group: "total", isTotal: true });
    (bucket.strata || []).forEach(r => rows.push({ ...r, group: "strata" }));
    kaumRows(key, ng).forEach(r => rows.push({ ...r, group: "kaum" }));

    let daerah = (DAERAH_SAMPLE[key] && DAERAH_SAMPLE[key][ng]) || [];
    const dpFilter = params && params.DP
      ? String(params.DP).split(",").map(s => s.trim()).filter(Boolean)
      : [];
    if (dpFilter.length) daerah = daerah.filter(r => dpFilter.includes(String(r.dp)));
    daerah.forEach(r => rows.push({ ...r, group: "list" }));
  }

  return { years, rows, daerahList: daerahListFor(endpoint, ng) };
}

// Kaum rows for a scope: the whole-Malaysia split (ng = null) or one negeri's split.
function kaumRows(key, ng) {
  const k = KAUM_SAMPLE[key];
  if (!k) return [];
  return (ng ? (k.perNegeri && k.perNegeri[ng]) : k.malaysia) || [];
}

// District dropdown items for a negeri, shape { dp, namaDP } — matches the JR2
// pages' daerahList. Empty for the Malaysia view (no negeri chosen).
function daerahListFor(endpoint, ng) {
  if (!ng) return [];
  const key = ENDPOINT_KEY[endpoint];
  const rows = (DAERAH_SAMPLE[key] && DAERAH_SAMPLE[key][ng]) || [];
  return rows.map(r => ({ dp: r.dp, namaDP: r.kategori }));
}

export default class kemudahanAsasServices {
  // endpoint: the JadualAwalan controller name for this amenity, e.g.
  //   "getJenisMilikanRumahLampiran" | "getBekalanAirLampiran" |
  //   "getBekalanElektrikLampiran" | "getKutipanSampahLampiran"
  // params: { NG (optional — absent/"ALL" = Malaysia), P (csv pusingan),
  //           S ("1"|"2"|"3"), W ("W"|"TW") }
  async getKemudahanAsas(endpoint, params = {}) {
    if (USE_SAMPLE) return sampleFor(endpoint, params);
    const res = await axios.get(
      `${process.env.VUE_APP_API_URL}/JadualAwalan/${endpoint}`,
      { params }
    );
    return (res.data && res.data.data) || {};
  }
}
